import { tr, usePartnerTranslation } from "../../lib/language";
import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { X, ZoomIn, ZoomOut, RotateCw } from "lucide-react";
import { Dialog, DialogContent } from "../ui/dialog";
import { cn } from "../../lib/utils";

/**
 * Minimal image cropper modal built on `react-easy-crop`.
 *
 * Props
 *   open        — boolean
 *   onClose     — () => void
 *   onApply     — (dataUrl: string) => void  (JPEG base64)
 *   imageSrc    — source dataURL / https URL to crop
 *   aspect      — crop ratio (1 for logo, 3.2 for banner)
 *   title       — header text
 *   outputSize  — { width, height } max output dimensions (rescaled proportionally)
 *   cropShape   — "rect" | "round"
 *   aspectOptions — [{ key, label, value }] optional preset buttons
 */
const ShopImageCropper = ({
  open,
  onClose,
  onApply,
  imageSrc,
  aspect = 1,
  aspectOptions = [],
  selectedAspectKey = "",
  onAspectChange,
  disableAspectOptions = false,
  helperText = "",
  title = "Adjust image",
  outputSize = { width: 800, height: 800 },
  preserveResolution = false,
  cropShape = "rect",
  applyLabel = "Apply",
}) => {
  usePartnerTranslation();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  React.useEffect(() => {
    if (open) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCroppedAreaPixels(null);
    }
  }, [open, imageSrc]);

  const apply = async () => {
    if (!croppedAreaPixels || !imageSrc) return;
    setProcessing(true);
    try {
      const dataUrl = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        rotation,
        preserveResolution ? { width: Infinity, height: Infinity } : outputSize,
      );
      onApply(dataUrl);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="shop-image-cropper"
        showClose={false}
        className="max-h-[92vh] max-w-[720px] overflow-hidden rounded-2xl border-[#EFE4E4] p-0"
      >
        <div className="flex items-center justify-between border-b border-[#EFE4E4] px-6 py-4">
          <h3 className="text-lg font-extrabold tracking-tight text-[#0F1115]">
            {title}
          </h3>
          <button
            type="button"
            data-testid="cropper-close"
            onClick={onClose}
            aria-label={tr("Close")}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[#6B7280] hover:bg-[#F3EEEE]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="relative h-[360px] w-full bg-[#0F1115]">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect}
              cropShape={cropShape}
              showGrid
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              objectFit="horizontal-cover"
              style={{
                containerStyle: { background: "#0F1115" },
                mediaStyle: { transition: "transform 0.15s" },
              }}
            />
          )}
        </div>

        <div className="space-y-5 bg-white px-6 py-5">
          {aspectOptions.length > 0 && (
            <div>
              <div className="mb-2 flex items-center justify-between gap-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#6B7280]">{tr("Crop format")}</p>
                {disableAspectOptions && (
                  <p className="text-xs text-[#9BA1AB]">{tr("Locked for this post")}</p>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {aspectOptions.map((option) => {
                  const active = selectedAspectKey === option.key;
                  return (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => !disableAspectOptions && onAspectChange?.(option.key)}
                      disabled={disableAspectOptions}
                      className={cn(
                        "h-9 rounded-md border px-2 text-xs font-semibold transition-colors",
                        active
                          ? "border-[#FF5757] bg-[#FFF1F1] text-[#9B0000]"
                          : "border-[#E6E8EC] text-[#0F1115] hover:border-[#0F1115]",
                        disableAspectOptions && !active && "opacity-50",
                      )}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
              {helperText && (
                <p className="mt-2 text-xs text-[#6B7280]">{helperText}</p>
              )}
            </div>
          )}

          <div className="flex items-center gap-3">
            <ZoomOut className="h-4 w-4 text-[#6B7280]" strokeWidth={1.8} />
            <input
              type="range"
              data-testid="cropper-zoom"
              min={1}
              max={3}
              step={0.02}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="humrahe-range flex-1"
              aria-label={tr("Zoom")}
            />
            <ZoomIn className="h-4 w-4 text-[#6B7280]" strokeWidth={1.8} />
          </div>

          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              data-testid="cropper-rotate"
              onClick={() => setRotation((r) => (r + 90) % 360)}
              className="inline-flex h-9 items-center gap-2 rounded-md border border-[#E6E8EC] px-3 text-xs font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115]"
            >
              <RotateCw className="h-3.5 w-3.5" strokeWidth={1.8} />{tr("Rotate 90°")}</button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                data-testid="cropper-cancel"
                className="inline-flex h-10 items-center rounded-md border border-[#E6E8EC] px-4 text-sm font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115]"
              >{tr("Cancel")}</button>
              <button
                type="button"
                onClick={apply}
                disabled={!croppedAreaPixels || processing}
                data-testid="cropper-apply"
                className={cn(
                  "humrahe-btn-primary inline-flex h-10 items-center rounded-md px-5 text-sm font-semibold",
                  (!croppedAreaPixels || processing) && "cursor-not-allowed opacity-60",
                )}
              >
                {processing ? tr("Applying…") : applyLabel}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShopImageCropper;

/* ---------- canvas helper ---------- */

function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", reject);
    image.crossOrigin = "anonymous";
    image.src = url;
  });
}

function getRadianAngle(deg) {
  return (deg * Math.PI) / 180;
}

/**
 * Returns a JPEG data URL of the cropped & rotated area, rescaled so the
 * longest edge fits within outputSize (keeping aspect).
 */
async function getCroppedImg(imageSrc, pixelCrop, rotation = 0, outputSize) {
  const image = await createImage(imageSrc);
  const rotRad = getRadianAngle(rotation);

  // Full-size canvas to accommodate the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotatedSize(
    image.width,
    image.height,
    rotation,
  );

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  const outputType = imageSrc.startsWith("data:image/png") ? "image/png" : "image/jpeg";
  if (outputType === "image/jpeg") { ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, canvas.width, canvas.height); }
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);

  // Crop to the selected area on the rotated canvas
  const cropX = Math.round(pixelCrop.x);
  const cropY = Math.round(pixelCrop.y);
  const cropW = Math.round(pixelCrop.width);
  const cropH = Math.round(pixelCrop.height);

  const cropped = ctx.getImageData(cropX, cropY, cropW, cropH);

  canvas.width = cropW;
  canvas.height = cropH;
  ctx.putImageData(cropped, 0, 0);

  // Explicit preview resizing remains available; master uploads retain the selected crop’s pixels.
  const scale = Math.min(
    1,
    outputSize.width / cropW,
    outputSize.height / cropH,
  );
  if (scale < 1) {
    const scaled = document.createElement("canvas");
    scaled.width = Math.round(cropW * scale);
    scaled.height = Math.round(cropH * scale);
    scaled
      .getContext("2d")
      .drawImage(canvas, 0, 0, scaled.width, scaled.height);
    return scaled.toDataURL(outputType, 0.94);
  }

  return canvas.toDataURL(outputType, 0.94);
}

function rotatedSize(width, height, rotation) {
  const rotRad = getRadianAngle(rotation);
  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}
